import React, { ReactElement, useEffect, useMemo } from 'react'
import useNavigation from 'core/hooks/useNavigation'
import { docCookies } from 'utils/docCookies'
import { Navigate } from 'react-router-dom'
import {
  DashboardUrl,
  SamlMobileAppRedirect,
  SltLoginUrl,
  StartUrl
} from 'constants/RouteUrls'
import useUrlIsAppRequest from 'core/hooks/useUrlIsAppRequest'

const ProtectedRoute = ({
  children,
  validateAuthOnly
}: {
  children?: ReactElement
  validateAuthOnly?: boolean
}) => {
  const { pathname, search, replace } = useNavigation()
  const isAppRequest = useUrlIsAppRequest()

  const slt = useMemo(() => new URLSearchParams(search).get('slt'), [search])
  const redirect_url = useMemo(
    () => new URLSearchParams(search).get('redirect_url'),
    [search]
  )

  const isAuthenticated = !!docCookies.getItem('auth_token')

  useEffect(() => {
    const timeout = setTimeout(() => {
      if (isAppRequest && !document.getElementById('is-app-request')) {
        const styleTag = document.createElement('style')
        document.head.appendChild(styleTag)
        styleTag.setAttribute('id', 'is-app-request')
        styleTag.innerText = '#ada-button-frame {display: none !important;}'
      }
    }, 300)
    return () => clearTimeout(timeout)
  }, [isAppRequest])

  // Validate protection only for auth flow
  if (validateAuthOnly) {
    const jwt = new URLSearchParams(search).get('jwt') ?? ''

    if (isAppRequest) {
      docCookies.removeItem('auth_token')
      document.querySelector('#ada-chat-button')?.classList.add('!hidden')
      window.location.href = SamlMobileAppRedirect.replaceParams({
        jwt
      })
      return null
    }
    if (pathname.includes('auth') && isAuthenticated) {
      return (
        <Navigate to={redirect_url ? redirect_url : DashboardUrl} replace />
      )
    }
    return <>{children}</>
  }

  if (isAuthenticated && redirect_url) {
    return <Navigate to={redirect_url} replace />
  }

  if (!isAuthenticated && slt) {
    return <Navigate to={`${SltLoginUrl}?slt=${slt}`} replace />
  }

  if (!isAuthenticated) {
    const withRedirect = new URLSearchParams(search)
    withRedirect.set('redirect_url', pathname)
    if (window.location.hash) {
      const hashPath = window.location.hash.slice(1)
      const [hashPathWithoutParams = '', urlQueryString = ''] =
        hashPath.split('?')
      if (StartUrl.endsWith(hashPathWithoutParams)) {
        return <Navigate to={`${StartUrl}?${urlQueryString}`} replace />
      }
    }
    return <Navigate to={`${StartUrl}?${withRedirect.toString()}`} replace />
  }

  return <>{children}</>
}

export default ProtectedRoute
